import React, { useCallback, useEffect, useState } from 'react';
import {
  Activity, Apple, Clipboard, Droplet, Dumbbell, HeartPulse, LayoutDashboard, LogOut,
  Moon, Scale, Sparkles, Sun, TrendingDown, User as UserIcon, Utensils, Video,
} from 'lucide-react';

import { api, clearToken, getToken } from './lib/api';
import { bmiBand, bodyMetrics, n1, today } from './lib/format';
import { Alert } from './components/ui';
import { UndoProvider, useUndo } from './lib/undo';

import Auth from './pages/Auth';
import Dashboard from './pages/Dashboard';
import FoodLog from './pages/FoodLog';
import Exercise from './pages/Exercise';
import Weight from './pages/Weight';
import Bloodwork from './pages/Bloodwork';
import WeightAI from './pages/WeightAI';
import DietAI from './pages/DietAI';
import Videos from './pages/Videos';
import Plans from './pages/Plans';
import Health from './pages/Health';
import Chat from './pages/Chat';
import Profile from './pages/Profile';

const NAV = [
  { group: 'Today', items: [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, title: 'Dashboard' },
    { id: 'food', label: 'Food Log', icon: Apple, title: 'Food Log' },
    { id: 'exercise', label: 'Exercise', icon: Dumbbell, title: 'Exercise' },
  ]},
  { group: 'Track', items: [
    { id: 'weight', label: 'Weight', icon: Scale, title: 'Weight & Measurements' },
    { id: 'bloodwork', label: 'Bloodwork', icon: Droplet, title: 'Bloodwork' },
    { id: 'health', label: 'Conditions', icon: HeartPulse, title: 'Health Conditions' },
  ]},
  { group: 'Intelligence', items: [
    { id: 'weight-ai', label: 'Weight AI', icon: TrendingDown, title: 'Weight AI' },
    { id: 'diet-ai', label: 'Diet AI', icon: Utensils, title: 'Diet AI' },
    { id: 'chat', label: 'AI Chat', icon: Sparkles, title: 'AI Health Assistant' },
    { id: 'videos', label: 'Videos', icon: Video, title: 'Videos' },
  ]},
  { group: 'You', items: [
    { id: 'plans', label: 'Plans', icon: Clipboard, title: 'Plans' },
    { id: 'profile', label: 'Profile', icon: UserIcon, title: 'Profile' },
  ]},
];

const ALL_TABS = NAV.flatMap((g) => g.items);

const THEME_KEY = 'fitlife_theme';

function AppShell() {
  const { undoableDelete } = useUndo();
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [tab, setTab] = useState('dashboard');
  const [theme, setTheme] = useState(() => localStorage.getItem(THEME_KEY) || 'dark');
  const [error, setError] = useState('');

  const [foodLog, setFoodLog] = useState([]);
  const [exerciseLog, setExerciseLog] = useState([]);
  const [weightLog, setWeightLog] = useState([]);
  const [conditions, setConditions] = useState([]);
  const [plan, setPlan] = useState(null);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem(THEME_KEY, theme);
  }, [theme]);

  useEffect(() => {
    if (!getToken()) { setChecking(false); return; }
    api.auth.me()
      .then(setUser)
      .catch(() => clearToken())
      .finally(() => setChecking(false));
  }, []);

  const refresh = useCallback(async () => {
    if (!user) return;
    try {
      const [food, exercise, weight, conds, activePlan, statsData] = await Promise.all([
        api.food.list(today()),
        api.exercise.list(today()),
        api.weight.list(100),
        api.conditions.list(),
        api.profile.getPlan(),
        api.profile.stats(),
      ]);
      setFoodLog(food);
      setExerciseLog(exercise);
      setWeightLog([...weight].reverse()); // API returns newest first; charts want oldest first
      setConditions(conds);
      setPlan(activePlan);
      setStats(statsData);
      setError('');
    } catch (e) {
      setError(e.message);
    }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  const signOut = () => {
    clearToken();
    setUser(null);
    setFoodLog([]); setExerciseLog([]); setWeightLog([]); setConditions([]);
    setPlan(null); setStats(null); setTab('dashboard');
  };

  if (checking) {
    return <div className="auth"><span className="spinner" /></div>;
  }

  if (!user) return <Auth onAuth={setUser} />;

  /* ── Mutations: update locally, then re-sync derived counts ── */

  const addFood = async (d) => {
    const created = await api.food.create(d);
    setFoodLog((l) => [created, ...l]);
    api.profile.stats().then(setStats).catch(() => {});
  };
  // Deleting a log row is one tap with no confirmation, which is fine on a
  // mouse and was not on a phone. Rather than adding a confirm step to every
  // deletion, the row goes immediately and the request is held for a few
  // seconds so it can be taken back. See lib/undo.jsx for why it defers the
  // delete rather than re-creating the row afterwards.
  const makeUndoableDelete = ({ list, setList, label, remove, after }) => (id) => {
    const index = list.findIndex((item) => item.id === id);
    if (index < 0) return;
    const item = list[index];
    undoableDelete({
      label,
      remove: () => setList((l) => l.filter((entry) => entry.id !== id)),
      restore: () => setList((l) => {
        if (l.some((entry) => entry.id === id)) return l;
        const next = [...l];
        next.splice(Math.min(index, next.length), 0, item);   // back where it was
        return next;
      }),
      commit: (opts) => remove(id, opts),
      onCommitted: after,
      onError: (err) => setError(err.message),
    });
  };

  const refreshStats = () => api.profile.stats().then(setStats).catch(() => {});

  const deleteFood = makeUndoableDelete({
    list: foodLog, setList: setFoodLog, label: 'Food entry',
    remove: api.food.remove, after: refreshStats,
  });

  const addExercise = async (d) => {
    const created = await api.exercise.create(d);
    setExerciseLog((l) => [created, ...l]);
    api.profile.stats().then(setStats).catch(() => {});
  };
  const deleteExercise = makeUndoableDelete({
    list: exerciseLog, setList: setExerciseLog, label: 'Exercise',
    remove: api.exercise.remove, after: refreshStats,
  });

  // Weighing in writes through to profile.current_weight, so refetch the user.
  const addWeight = async (d) => {
    const created = await api.weight.create(d);
    setWeightLog((l) => [...l, created]);
    api.auth.me().then(setUser).catch(() => {});
    api.profile.stats().then(setStats).catch(() => {});
  };
  const deleteWeight = makeUndoableDelete({
    list: weightLog, setList: setWeightLog, label: 'Weigh-in',
    remove: api.weight.remove,
    // Deleting a weigh-in re-derives profile.current_weight server-side, so the
    // user has to be refetched — but only once the deletion actually commits.
    after: () => { api.auth.me().then(setUser).catch(() => {}); refreshStats(); },
  });

  const addCondition = async (d) => {
    const created = await api.conditions.create(d);
    setConditions((l) => [...l, created]);
  };
  const deleteCondition = makeUndoableDelete({
    list: conditions, setList: setConditions, label: 'Condition',
    remove: api.conditions.remove,
  });

  const saveProfile = async (d) => setUser(await api.profile.update(d));
  const setDiet = async (diet_plan) => setPlan(await api.profile.setPlan({ diet_plan }));
  const setWorkout = async (workout_plan) => setPlan(await api.profile.setPlan({ workout_plan }));

  const current = ALL_TABS.find((t) => t.id === tab) || ALL_TABS[0];
  const latest = weightLog[weightLog.length - 1];
  const { bmi, ffmi } = bodyMetrics(user.profile, latest);
  const band = bmiBand(bmi);
  const initials = (user.full_name || user.username || '?')
    .split(' ').map((s) => s[0]).slice(0, 2).join('').toUpperCase();

  const PAGES = {
    dashboard: <Dashboard user={user} foodLog={foodLog} exerciseLog={exerciseLog}
      weightLog={weightLog} stats={stats} plan={plan} onGo={setTab} />,
    food: <FoodLog foodLog={foodLog} onAdd={addFood} onDelete={deleteFood} />,
    exercise: <Exercise exerciseLog={exerciseLog} onAdd={addExercise} onDelete={deleteExercise} />,
    weight: <Weight weightLog={weightLog} onAdd={addWeight} onDelete={deleteWeight} />,
    bloodwork: <Bloodwork />,
    health: <Health conditions={conditions} onAdd={addCondition} onDelete={deleteCondition} />,
    'weight-ai': <WeightAI />,
    'diet-ai': <DietAI />,
    chat: <Chat user={user} />,
    videos: <Videos />,
    plans: <Plans plan={plan} onSelectDiet={setDiet} onSelectWorkout={setWorkout} />,
    profile: <Profile user={user} stats={stats} weightLog={weightLog} onSave={saveProfile} />,
  };

  return (
    <div className="shell">
      <aside className="sidebar">
        <div className="brand">
          <span className="brand-mark"><Activity size={17} /></span>
          <span>FitLife Pro</span>
        </div>

        <nav className="nav">
          {NAV.map((group) => (
            <React.Fragment key={group.group}>
              <div className="nav-group">{group.group}</div>
              {group.items.map((item) => (
                <button
                  key={item.id}
                  className={`nav-item${tab === item.id ? ' active' : ''}`}
                  onClick={() => setTab(item.id)}
                  aria-current={tab === item.id ? 'page' : undefined}
                  title={item.label}
                >
                  <item.icon size={16} />
                  <span>{item.label}</span>
                </button>
              ))}
            </React.Fragment>
          ))}
        </nav>

        <div className="sidebar-foot">
          <div className="who">
            <span className="avatar">{initials}</span>
            <div style={{ minWidth: 0 }}>
              <div className="who-name">{user.full_name || user.username}</div>
              <div className="who-mail">{user.email}</div>
            </div>
          </div>
          <button className="btn btn-quiet btn-sm" style={{ width: '100%', justifyContent: 'flex-start' }}
            onClick={signOut}>
            <LogOut size={14} /><span>Sign out</span>
          </button>
        </div>
      </aside>

      <div className="main">
        <header className="topbar">
          <div>
            <div className="topbar-title">{current.title}</div>
            <div className="topbar-sub">
              {new Date().toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' })}
            </div>
          </div>

          <div className="row-tight" style={{ gap: 10 }}>
            <div className="metrics">
              <div className="metric">
                <div className="metric-v" style={{ color: `var(--${band.tone === 'muted' ? 'text' : band.tone})` }}>
                  {bmi ? n1(bmi) : '–'}
                </div>
                <div className="metric-k">BMI</div>
              </div>
              <div className="metric">
                <div className="metric-v">{ffmi ? n1(ffmi) : '–'}</div>
                <div className="metric-k">FFMI</div>
              </div>
              <div className="metric">
                <div className="metric-v">{latest ? n1(latest.weight) : '–'}</div>
                <div className="metric-k">kg</div>
              </div>
            </div>
            <button className="btn btn-quiet btn-icon"
              onClick={() => setTheme((t) => (t === 'dark' ? 'light' : 'dark'))}
              aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
              title="Toggle theme">
              {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
            </button>
          </div>
        </header>

        {error && (
          <div style={{ padding: 'var(--gap-3) var(--gap-4) 0' }}>
            <Alert tone="crit">{error}</Alert>
          </div>
        )}

        {PAGES[tab]}
      </div>
    </div>
  );
}

export default function App() {
  return (
    <UndoProvider>
      <AppShell />
    </UndoProvider>
  );
}
